'use client';

import { useEffect, useState } from 'react';
import Link from 'next/link';
import { Flame, Footprints, Beef, TrendingUp, Dumbbell, PersonStanding, ChevronRight } from 'lucide-react';
import { getDailySummary } from '@/lib/nutritionService';
import { getLocalDateString } from '@/lib/db';
import type { DailyCalorieSummary, DailyActivity } from '@/lib/types/activity';
import type { DailyNutritionSummary } from '@/lib/types/nutrition';

interface DailyStatusRowProps {
  userId?: string;
  todayActivity: DailyActivity | null;
  dailyCalories: DailyCalorieSummary | null;
}

type ExpandedPill = 'cal' | 'steps' | 'protein' | null;

export default function DailyStatusRow({ userId, todayActivity, dailyCalories }: DailyStatusRowProps) {
  const [nutritionData, setNutritionData] = useState<DailyNutritionSummary | null>(null);
  const [expanded, setExpanded] = useState<ExpandedPill>(null);

  useEffect(() => {
    if (!userId) return;
    const today = getLocalDateString();
    getDailySummary(userId, today).then(data => {
      if (data.entries.length > 0) {
        setNutritionData(data);
      }
    }).catch(() => {});
  }, [userId]);

  // Treat zero-value records as "no data" — Dexie may have a sparse Google Fit row with all 0s
  const hasCalorieData = dailyCalories && dailyCalories.totalCalories > 0;
  const hasActivityData = todayActivity && todayActivity.steps > 0;

  const totalCal = hasCalorieData ? Math.round(dailyCalories!.totalCalories) : null;
  const steps = hasActivityData ? todayActivity!.steps : null;
  const protein = nutritionData ? Math.round(nutritionData.totalProtein) : null;

  const toggle = (key: ExpandedPill) => {
    setExpanded(prev => prev === key ? null : key);
  };

  const pillBase = 'flex-shrink-0 flex items-center gap-1.5 px-3 py-2 rounded-full border transition-all cursor-pointer select-none';

  return (
    <div className="space-y-2">
      {/* Pills row */}
      <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1">
        {/* Calories */}
        <button
          onClick={() => toggle('cal')}
          className={`${pillBase} ${expanded === 'cal' ? 'bg-orange-500/20 border-orange-400/60' : 'bg-orange-500/10 border-orange-500/30'}`}
        >
          <span className="text-orange-400"><Flame className="w-3.5 h-3.5" /></span>
          <span className={`text-xs font-semibold ${totalCal ? 'text-white' : 'text-gray-500'}`}>
            {totalCal ? totalCal.toLocaleString() : '\u2014'}
          </span>
          <span className="text-[10px] text-gray-500">Cal</span>
        </button>

        {/* Steps */}
        <button
          onClick={() => toggle('steps')}
          className={`${pillBase} ${expanded === 'steps' ? 'bg-blue-500/20 border-blue-400/60' : 'bg-blue-500/10 border-blue-500/30'}`}
        >
          <span className="text-blue-400"><Footprints className="w-3.5 h-3.5" /></span>
          <span className={`text-xs font-semibold ${steps ? 'text-white' : 'text-gray-500'}`}>
            {steps ? steps.toLocaleString() : '\u2014'}
          </span>
          <span className="text-[10px] text-gray-500">Steps</span>
        </button>

        {/* Protein */}
        <button
          onClick={() => toggle('protein')}
          className={`${pillBase} ${expanded === 'protein' ? 'bg-red-500/20 border-red-400/60' : 'bg-red-500/10 border-red-500/30'}`}
        >
          <span className="text-red-400"><Beef className="w-3.5 h-3.5" /></span>
          <span className={`text-xs font-semibold ${protein ? 'text-white' : 'text-gray-500'}`}>
            {protein ? `${protein}g` : '\u2014'}
          </span>
          <span className="text-[10px] text-gray-500">Protein</span>
        </button>

        {/* Assessment — always a link, no expand */}
        <Link
          href="/assessments"
          className={`${pillBase} bg-green-500/10 border-green-500/30 hover:opacity-80`}
        >
          <span className="text-green-400"><TrendingUp className="w-3.5 h-3.5" /></span>
          <span className="text-xs font-semibold text-white">View</span>
          <span className="text-[10px] text-gray-500">Assessment</span>
        </Link>
      </div>

      {/* Expanded detail cards */}
      {expanded === 'cal' && (
        <div className="rounded-lg border border-orange-500/30 bg-orange-500/5 p-3">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-bold uppercase tracking-wide text-orange-400">Calorie Breakdown</span>
          </div>
          {hasCalorieData ? (
            <>
              <div className="grid grid-cols-2 gap-3">
                <div className="flex items-center gap-2">
                  <Dumbbell className="w-3.5 h-3.5 text-orange-300" />
                  <div>
                    <div className="text-sm font-semibold text-white">{Math.round(dailyCalories!.workoutCalories)}</div>
                    <div className="text-[10px] text-gray-500">Workout</div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <PersonStanding className="w-3.5 h-3.5 text-orange-300" />
                  <div>
                    <div className="text-sm font-semibold text-white">{Math.round(dailyCalories!.neatCalories)}</div>
                    <div className="text-[10px] text-gray-500">NEAT</div>
                  </div>
                </div>
              </div>
              {dailyCalories!.sessions.length > 0 && (
                <div className="mt-2 pt-2 border-t border-orange-500/20">
                  <div className="text-[10px] text-gray-500">
                    {dailyCalories!.sessions.length} session{dailyCalories!.sessions.length !== 1 ? 's' : ''} today
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="text-xs text-gray-500 py-1">
              No calorie data yet. Complete a workout or connect a wearable.
            </div>
          )}
        </div>
      )}

      {expanded === 'steps' && (
        <div className="rounded-lg border border-blue-500/30 bg-blue-500/5 p-3">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-bold uppercase tracking-wide text-blue-400">Activity Details</span>
          </div>
          {hasActivityData ? (
            <>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <div className="text-sm font-semibold text-white">{todayActivity!.steps.toLocaleString()}</div>
                  <div className="text-[10px] text-gray-500">Steps</div>
                </div>
                <div>
                  <div className="text-sm font-semibold text-white">{todayActivity!.activeMinutes}m</div>
                  <div className="text-[10px] text-gray-500">Active</div>
                </div>
                <div>
                  <div className="text-sm font-semibold text-white">{todayActivity!.activeCalories}</div>
                  <div className="text-[10px] text-gray-500">Active Cal</div>
                </div>
              </div>
              <div className="mt-2 pt-2 border-t border-blue-500/20">
                <div className="flex items-center justify-between text-[10px] text-gray-500 mb-1">
                  <span>Daily goal: 7,000</span>
                  <span className={todayActivity!.steps >= 7000 ? 'text-green-400' : 'text-gray-400'}>
                    {Math.min(100, Math.round((todayActivity!.steps / 7000) * 100))}%
                  </span>
                </div>
                <div className="h-1.5 bg-operator-gunmetal rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all ${todayActivity!.steps >= 7000 ? 'bg-green-400' : 'bg-blue-400'}`}
                    style={{ width: `${Math.min(100, (todayActivity!.steps / 7000) * 100)}%` }}
                  />
                </div>
              </div>
            </>
          ) : (
            <div className="text-xs text-gray-500 py-1">
              No step data yet. Connect Google Fit in <Link href="/settings" className="text-operator-gold hover:underline">Settings</Link> to track activity.
            </div>
          )}
        </div>
      )}

      {expanded === 'protein' && (
        <div className="rounded-lg border border-red-500/30 bg-red-500/5 p-3">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-bold uppercase tracking-wide text-red-400">Macros Today</span>
            <Link href="/fuel" className="text-[10px] text-operator-gold hover:underline flex items-center gap-0.5">
              Log Food <ChevronRight className="w-3 h-3" />
            </Link>
          </div>
          {nutritionData ? (
            <div className="grid grid-cols-4 gap-2">
              <MacroMini label="Calories" current={Math.round(nutritionData.totalCalories)} goal={nutritionData.goalCalories} color="bg-orange-400" />
              <MacroMini label="Protein" current={Math.round(nutritionData.totalProtein)} goal={nutritionData.goalProtein} unit="g" color="bg-red-400" />
              <MacroMini label="Carbs" current={Math.round(nutritionData.totalCarbs)} goal={nutritionData.goalCarbs} unit="g" color="bg-yellow-400" />
              <MacroMini label="Fat" current={Math.round(nutritionData.totalFat)} goal={nutritionData.goalFat} unit="g" color="bg-purple-400" />
            </div>
          ) : (
            <div className="text-xs text-gray-500 py-1">
              No meals logged today. Tap <Link href="/fuel" className="text-operator-gold hover:underline">Log Food</Link> to start tracking.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function MacroMini({ label, current, goal, unit, color }: {
  label: string;
  current: number;
  goal: number;
  unit?: string;
  color: string;
}) {
  const pct = Math.min((current / Math.max(goal, 1)) * 100, 100);
  return (
    <div>
      <div className="text-sm font-semibold text-white">
        {current}{unit ?? ''}
      </div>
      <div className="text-[10px] text-gray-500 mb-1">{label}</div>
      <div className="h-1 bg-operator-gunmetal rounded-full overflow-hidden">
        <div className={`h-full rounded-full ${color}`} style={{ width: `${pct}%` }} />
      </div>
      <div className="text-[9px] text-gray-600 mt-0.5">/ {goal}{unit ?? ''}</div>
    </div>
  );
}
